<script lang="ts" setup>
import { onBeforeMount, ref } from "vue";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { useRouter, useRoute } from "vue-router";
import ThreadPost from "@/components/mobile/post/ThreadPost.vue";
import { ThreadPostInstance } from "@/types/component";
import api from "@/modules/api";
import { useMate } from "@/stores/mate";
import { useMedia } from "@/stores/media";
import { useMoment } from "@/stores/moment";
import { IThreadPostItem } from "@/components/mobile/post/ThreadPostItem.vue";
import GlobalScrollLayout from "@/components/mobile/layout/GlobalScrollLayout.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import i18n from "@/locales";
import { StorageGet, StorageRemove } from "@/tools/localStorage";
import {
  sessionStorageGet,
  sessionStorageRemove,
  sessionStorageSet,
} from "@/tools/sessionStorage";
import { checkMateMediaPayment } from "@/tools/payment";
import { copyObjRemoveNullValue } from "@/tools/obj";
import { useUI } from "@/stores/ui";
import { syncMomentToTimelineEvent } from "@/apis/user";
import { useWorldview } from "@/stores/worldview";
import { Moment, Visibility } from "@/types/entity";
import { removeHtmlTag } from "@/tools/text";

// 内部状态
const router = useRouter();
const route = useRoute();
const mateStore = useMate();
const momentStore = useMoment();
const threadPostRef = ref<ThreadPostInstance | null>(null);
const isPosting = ref(false);
const loading = ref(false);
const items = ref<IThreadPostItem[]>([]);
const draftUuidList = ref<string[]>([]);
// Lifecycle
const moment2ThreadItem = (moment: Moment[]) => {
  return moment
    .sort(
      (a, b) =>
        new Date(a.dtCreate!).getTime() - new Date(b.dtCreate!).getTime()
    )
    .map((item) => {
      const { text, mediazz, chatMessagezz } = item;
      const imgList = mediazz.filter((item) => item.type === 1);
      const videoList = mediazz.filter((item) => item.type === 2);

      //@ts-ignore
      return {
        ...item,
        message: text,
        imgList: imgList.map((item) => ({
          mediaId: item.id,
          status: "done",
          ...item,
        })),
        videoList: videoList.map((item) => ({
          mediaId: item.id,
          status: "done",
          ...item,
        })),
        sharedMessageList: chatMessagezz.map((item) => ({
          id: item.id,
          isCurrentUser: item.mate.id === mateStore.currentMate!.id,
          msg: item.text,
          isSelected: true,
        })),
        visibility: item.visibility,
        mentionMateIdzz: item.mentionMatezz,
        storyId: item.story?.id,
      } as IThreadPostItem;
    });
};
const isEdit = ref(false);
onBeforeMount(async () => {
  loading.value = true;
  try {
    const { uuid } = route.query;
    if (uuid) {
      isEdit.value = true;
      const item = await momentStore.getOrFetchMomentItem(uuid as string);
      items.value = moment2ThreadItem([item!]);
      selectCollectionList.value = item?.collectionzz?.map((v) => v.id!) || [];
      return;
    }

    const draftMoments = await momentStore.getDraftMoments(
      mateStore.currentMate!.id!
    );
    let item = sessionStorageGet("import-moment");
    if (item) {
      sessionStorageRemove("import-moment");
      return (items.value = item);
    }

    item = StorageGet("threadItemList:" + mateStore.currentMate?.id);
    if (item) return (items.value = item);

    draftUuidList.value = draftMoments.map((item) => item.uuid);
    selectCollectionList.value =
      draftMoments?.[0]?.collectionzz?.map((v) => v.id!) || [];
    items.value = moment2ThreadItem(draftMoments);
  } finally {
    loading.value = false;
  }
});

// Event Handler
const getMomentParams = (
  item: IThreadPostItem,
  visibility: Visibility,
  status: Moment["status"] = 1,
  parentId?: number,
  collectionIdzz?: number[]
) => {
  // 1. 校验
  const {
    message,
    imgList,
    videoList,
    sharedMessageList,
    mentionMateIdzz,
    storyId,
    isAiGenerated,
    audioMedia,
  } = item;
  const mediaIdzz = [];
  if (imgList.length > 0) {
    mediaIdzz.push(...imgList.map((item) => item.mediaId));
  } else if (videoList.length > 0) {
    mediaIdzz.push(...videoList.map((item) => item.mediaId));
  }

  return {
    mateId: mateStore.currentMate!.uuid,
    status,
    text: message,
    mentionMateIdzz: mentionMateIdzz.map((item) => item.uuid),
    mediaIdzz,
    visibility,
    collectionIdzz,
    messageIdzz: sharedMessageList.map((item) => item.id),
    parentId,
    storyId,
    isAiGenerated,
    audioMediaId: audioMedia?.id,
  };
};
const validateThreadPostITem = (item: IThreadPostItem) => {
  const { message, imgList, videoList, sharedMessageList } = item;
  return (
    removeHtmlTag(message) !== "" ||
    imgList.filter((item) => item.status !== "failed").length > 0 ||
    videoList.filter((item) => item.status !== "failed").length > 0 ||
    sharedMessageList.length > 0
  );
};

const checkMediazz = async (item: IThreadPostItem) => {
  const mediazz: {
    crystalPrice?: number;
    visibility: Visibility;
    mediaId: number;
  }[] = [];

  for (const img of item.imgList) {
    if (!checkMateMediaPayment(img.visibility!, img.crystalPrice!))
      return false;
    mediazz.push({
      crystalPrice: img.crystalPrice || undefined,
      visibility: img.visibility || 1,
      mediaId: img.mediaId,
    });
  }

  for (const video of item.videoList) {
    if (!checkMateMediaPayment(video.visibility!, video.crystalPrice!))
      return false;
    mediazz.push({
      crystalPrice: video.crystalPrice || undefined,
      visibility: video.visibility || 1,
      mediaId: video.mediaId,
    });
  }
  let promises: Promise<any>[] = [];
  mediazz.length && promises.push(api.createOrUpdateManyMateMedia(mediazz));
  if (StorageGet("shouldUpdateSuperAdmirePrice")) {
    StorageRemove("shouldUpdateSuperAdmirePrice");
    mateStore.updateMateDetail(mateStore.currentMate?.uuid!, {
      ...copyObjRemoveNullValue(mateStore.currentMate),
      superAdmirePrice: mateStore.currentMate!.superAdmirePrice,
    });
  }
  await Promise.all(promises);
  return true;
};
let isPosted = false;
const onConfirm = async (status: Moment["status"] = 1) => {
  // 1. 校验
  const { items, visibility } = threadPostRef.value!;
  const validItems = items.filter(validateThreadPostITem);
  if (validItems.length === 0) {
    showFailToast(i18n.global.t("Message.nothingWasWritten"));
    return;
  }
  const mediaStore = useMedia();
  const isSomeMediaUploading = validItems.some((item) => {
    const { imgList, videoList } = item;
    return !mediaStore.validateMediaListElseToast(false, imgList, videoList);
  });
  if (isSomeMediaUploading) {
    return;
  }
  const isDraft = status === 2;
  const loadingToast = showLoadingToast({
    message: isDraft
      ? i18n.global.t("Message.savingProgress")
      : i18n.global.t("Message.publishing"),
  });
  try {
    sessionStorageSet("reloadMateHome", 1);
    if (isEdit.value) {
      const params = getMomentParams(
        validItems[0],
        visibility,
        status,
        undefined,
        selectCollectionList.value
      );
      const isOk = await checkMediazz(validItems[0]);
      if (!isOk) return;
      const { item } = await api.updateMoment(
        route.query.uuid as string,
        params
      );
      const moment = useMoment();
      await moment.initMomentListIfEmpty(true, 0);
      showSuccessToast(i18n.global.t("Message.saveSuccess"));
      router.back();
      return;
    }

    isPosting.value = true;
    // 2. 发布
    let parentId: number | undefined = undefined;
    const savedMomentList: Moment[] = [];
    for (let idx in validItems) {
      const item = validItems[idx];
      const params = getMomentParams(
        item,
        visibility,
        status,
        parentId,
        selectCollectionList.value
      );
      const isOk = await checkMediazz(item);
      if (!isOk) return;
      const { item: moment } = await api.createMoment(params);
      parentId = moment.id;
      savedMomentList.push(moment);
    }
    StorageRemove("threadItemList:" + mateStore.currentMate?.id);
    // 2.1 删除草稿
    if (draftUuidList.value.length > 0) {
      await momentStore.deleteDraftMoment(draftUuidList.value);
      draftUuidList.value = [];
    }
    if (isDraft) {
      // 3.1 若为暂存，则保存草稿
      draftUuidList.value = savedMomentList.map((item) => item.uuid);
      loadingToast.close();
      showSuccessToast(i18n.global.t("Message.saveSuccess"));
    } else {
      // 3.2 刷新promote列表
      const moment = useMoment();
      if (mateStore.currentMate?.id !== mateStore.userMate.id) {
        const targetTimelines = [];
        if (isSyncWorldviewTimeline.value) {
          targetTimelines.push("worldview");
        }
        if (isSyncMateTimeline.value) {
          targetTimelines.push("mate");
        }
        if (targetTimelines.length > 0) {
          await syncMomentToTimelineEvent({
            mateId: mateStore.currentMate!.id!,
            momentIds: savedMomentList.map((item) => item.id!),
            targetTimelines,
          });
        }
      }
      await Promise.all([
        moment.initMomentListIfEmpty(true, 0),
        moment.initMomentListIfEmpty(true, 2),
      ]);

      loadingToast.close();
      showSuccessToast(i18n.global.t("Message.publishSuccess"));
      isPosted = true;
      router.back();
      // router.replace({
      //   name: "threads",
      //   query: {
      //     tab: "threads",
      //     sub: "0",
      //   },
      // });
    }
  } catch (e) {
    loadingToast.close();
    showFailToast(
      `${i18n.global.t("Message.oh")} ${
        isDraft
          ? i18n.global.t("Message.saveFailed")
          : i18n.global.t("Message.publishFailed")
      }`
    );
    throw e;
  } finally {
    isPosting.value = false;
    loadingToast.close();
  }
};

const uiStore = useUI();
const clickSwitchCharacters = () => {
  uiStore.popMateSelector();
};
const isSyncWorldviewTimeline = ref(false);
const isSyncMateTimeline = ref(false);
const worldviewStore = useWorldview();
// onBeforeRouteLeave(() => {
//   if (isPosted) {
//     return true;
//   }
//   // 这里用showConfirmDialog会有问题（因为需要Promise）
//   return confirm(i18n.global.t("ConfirmModal.cancelPublishInfo"));
// });
const selectCollectionList = ref<number[]>([]);
</script>
<template>
  <global-scroll-layout>
    <template #nav>
      <threads-nav show-back :show-center="false">
        <!-- <template #center>
          <div class="font-bold flex gap-1 items-center w-full">
            <span class="line-clamp-1 flex-1">
              {{ mateStore.currentMate!.name }}
            </span>
          </div>
        </template> -->
        <template #right>
          <button
            v-if="!isEdit"
            class="btn btn-xs rounded"
            @click="clickSwitchCharacters"
          >
            {{ $t(`Buttons.switchCharacters`) }}
          </button>
          <button
            v-if="!isEdit"
            :disabled="!threadPostRef || isPosting"
            class="btn btn-xs rounded"
            @click="onConfirm(2)"
          >
            {{ $t("Buttons.momentSave") }}
          </button>
          <button
            :disabled="!threadPostRef || isPosting"
            class="btn btn-xs btn-primary rounded"
            @click="onConfirm(1)"
          >
            {{ $t(isEdit ? "Buttons.save" : "Buttons.publish") }}
          </button>
        </template>
      </threads-nav>
    </template>
    <template #default>
      <div v-if="loading" class="flex justify-center items-center h-full">
        <span class="loading"></span>
      </div>
      <template v-else>
        <thread-post
          ref="threadPostRef"
          :enable-cache="!isEdit"
          :initial-items="items"
          class="p-2"
        />

        <div
          class="px-4 flex flex-col gap-2"
          v-if="mateStore.currentMate?.id !== mateStore.userMate.id"
        >
          <div class="flex gap-2 items-center">
            <i class="iconify mingcute--refresh-3-line"></i>
            <span> {{ $t("Moment.sync") }} </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="flex-1">
              {{
                $t("Moment.syncWorldviewTimeline", {
                  name: worldviewStore.worldviewMap[
                    mateStore.currentMate!.worldviewId!
                  ].name,
                })
              }}
            </span>
            <VanSwitch :size="20" v-model="isSyncWorldviewTimeline" />
          </div>

          <div class="flex justify-between items-center">
            <span class="flex-1">
              {{ $t("Moment.syncMateTimeline") }}
            </span>
            <VanSwitch :size="20" v-model="isSyncMateTimeline" />
          </div>
        </div>

        <modal-select-collection
          :selected="selectCollectionList"
          @confirm="selectCollectionList = $event"
        />
      </template>
    </template>
  </global-scroll-layout>
</template>
